import { useState, useContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CREATE_ACTIVITY_MUTATION } from 'graphql/APICalls';
import { MapContext, MapView } from 'providers/MapProvider';
import { DataContext } from 'providers/DataProvider';
import { ME_QUERY } from 'graphql/APICalls';
import DatePicker from 'react-datepicker';
import { dateTimeFormat } from 'pages/Activity';
import 'react-datepicker/dist/react-datepicker.css';

import 'styles/CreateActivity.css';

// TODO: on activity creation, prompt user to turn on notifications

function CreateActivity(){

  const { me } = useContext(DataContext);
  const { map, updateLocation } = useContext(MapContext);

  const navigate = useNavigate();

  // Title, description, and location name states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [locationName, setLocationName] = useState('');

  // Start date state
  const [startDate, setStartDate] = useState(null);
  const startDateValue =
    useMemo(() =>
      (startDate && (startDate > new Date()) ? dateTimeFormat.format(startDate) : 'now'),
      [startDate]);
  const handleStartDateChange = date => setStartDate(date > new Date() ? date : null);

  // End date state
  const [endDate, setEndDate] = useState(null);
  const endDateValue =
    useMemo(() =>
      (endDate ? dateTimeFormat.format(endDate) : 'never'),
      [endDate]);
  const handleEndDateChange = date => {
    if (date > new Date()) { setEndDate(date) }
    else { setEndDate(null); alert('End date must be in the future.') }
    };

  // Visibility start date state
  const [visibilityStartDate, setVisibilityStartDate] = useState(null);
  const visibilityStartDateValue =
    useMemo(() =>
      (visibilityStartDate < new Date() ? 'now' : dateTimeFormat.format(visibilityStartDate)),
      [visibilityStartDate]);
  const handleVisibilityStartDateChange = date => setVisibilityStartDate(date > new Date() ? date : null);



  const [sendCreateActivityMutation, createActivityMutation] = useMutation(CREATE_ACTIVITY_MUTATION);

  const [activityGroups, setActivityGroups] = useState(new Set());

  const toggleGroup = (groupId) => {
    setActivityGroups(activityGroups.symmetricDifference(new Set([groupId])));
  }


  const [visibility, setVisibility] = useState('Public');

  const [errorMessage, setErrorMessage] = useState('');

  const handleActivityCreation = async (e) => {

    e.preventDefault();

    if (!title.length)
      { setErrorMessage("Don't forget to set a title for the activity!"); return }
    else if (!locationName.length)
      { setErrorMessage("Don't forget to name the location where you'll be doing this!"); return }
    else if (visibility==='Private' && activityGroups.size===0)
      { setErrorMessage('For a private activity you need to select at least one group that the activity will be visible to.'); return }

    sendCreateActivityMutation(
      { variables:
          { title, description,
            location: {name: locationName, geometry: { coordinates: map.selectedLocation } },
            groups: [...activityGroups],
            startDate, endDate, visibilityStartDate},
        onCompleted:
          data => { updateLocation(); navigate(`/t/${data.createActivity.shortId}`) },
        refetchQueries: [ { query: ME_QUERY } ] }, // TODO: make more efficient
    ).catch(()=>{});
  };

  const meJoinedGroups = useMemo(() => me?.groups?.filter(group => group.status === 'joined'), [me]);

  return (
    <div className="auto-scrollbar">
      <div className="centerer create-activity-overlay">
        <form className="centered form-1" onSubmit={handleActivityCreation}>
          <label>Title:</label>
          <input type="text" value={title} autoCapitalize="sentences" onChange={(e) => setTitle(e.target.value)} />
          <label>Description:</label>
          <input type="text" value={description} autoCapitalize="sentences" onChange={(e) => setDescription(e.target.value)} />
          <label>Location name</label>
          <input type="text" value={locationName} autoCapitalize="words" onChange={(e) => setLocationName(e.target.value)} />
          <label>Location:</label>
          <MapView id={"location_selector"} options={{locationSelector: true, activityMarkers: false, indicatedActivity: false}}/>
          <label>Visibility:</label>
          <div className="public-private-toggle">
            {
              ['Public','Private'].map(option =>
                <button key={option} type="button"
                  className={visibility===option ? "active" : null }
                  onClick={()=>{
                    setVisibility(option);
                    if (option==='Public') setActivityGroups(new Set()) }}>
                    {option}
                </button>)
            }
          </div>
          { visibility==='Private' && <>
            <label>{meJoinedGroups && !!meJoinedGroups.length && 'Only visible to:'}</label>
            <div className="group-selector">
              {meJoinedGroups && !meJoinedGroups.length &&
                <label>
                  You don't have any groups to select from yet!<br/>
                  Get started creating private activities by creating or joining a group.
                </label>
              }
              {meJoinedGroups && meJoinedGroups.map(group =>
                <button
                  key = {group.id}
                  onClick = {e=>{e.preventDefault(); toggleGroup(group.id)}}
                  className = {"group-selector-button " + (activityGroups.has(group.id) ? 'active' : '')}>
                    <input
                    type="checkbox"
                    checked={activityGroups.has(group.id)}
                    style={{ marginRight: '10px', pointerEvents: 'none' }}
                    readOnly={true}
                    />
                    {group.name}
                </button>)
              }
            </div>
          </>}
          <label>Starts:</label>
          <DatePicker selected={startDate} onChange={handleStartDateChange} showTimeSelect dateFormat="Pp" value={startDateValue} onFocus={e=>e.target.readOnly=true}/>
          <label>Ends:</label>
          <DatePicker selected={endDate}   onChange={handleEndDateChange} showTimeSelect dateFormat="Pp" value={endDateValue} onFocus={e=>e.target.readOnly=true}/>
          <label>Visible starting:</label>
          <DatePicker selected={visibilityStartDate} onChange={handleVisibilityStartDateChange} showTimeSelect dateFormat="Pp" value={visibilityStartDateValue} onFocus={e=>e.target.readOnly=true}/>
          <div className="wide error-message" style={{display: errorMessage.length ? 'block' : 'none'}}>{errorMessage}</div>
          <button type="submit" disabled={createActivityMutation.loading}>Create activity</button>
          {createActivityMutation.loading && <p>Loading...</p>}
          {createActivityMutation.error   && <p>Error: {createActivityMutation.error.message}</p>}
          {createActivityMutation.data?.createActivity?.id && <p>Created activity with ID {createActivityMutation.data?.createActivity?.id}</p>}
        </form>
      </div>
    </div>
    );
}

export default CreateActivity